// Billing client — fetches plan + usage, creates orders, opens Razorpay Checkout.
//
// Architecture:
//   - Pricing config is fetched from GET /api/billing/pricing (public).
//   - Plan + usage from GET /api/billing/me (auth required).
//   - Checkout flow:
//       1. POST /api/billing/create-order { currency, billing_cycle } -> {order_id, amount, currency, key_id}
//       2. Open Razorpay Checkout SDK with those params
//       3. On success handler -> POST /api/billing/verify-payment { signature }
//       4. Refresh local plan from /api/billing/me
//   - Webhook also flips status='active' independently (defense in depth).
import { getAccessToken } from "@/lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

let _scriptPromise = null;
export function loadRazorpayScript() {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve(true);
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { _scriptPromise = null; resolve(false); };
    document.body.appendChild(s);
  });
  return _scriptPromise;
}

async function authedFetch(path, opts = {}) {
  const token = await getAccessToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const d = await res.json();
      detail = typeof d.detail === "string" ? d.detail : (d.detail?.message || detail);
    } catch { /* ignore */ }
    const err = new Error(detail);
    err.status = res.status;
    try { err.body = await res.clone().json(); } catch { /* ignore */ }
    throw err;
  }
  return res.json();
}

/** Public pricing config — no auth required. */
export async function fetchPricing() {
  const res = await fetch(`${API}/billing/pricing`);
  if (!res.ok) throw new Error("Couldn't load pricing");
  return res.json();
}

/** Authenticated plan + usage. Returns null if 401 (anon). */
export async function fetchBillingMe() {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    return await authedFetch("/billing/me");
  } catch (e) {
    if (e.status === 401) return null;
    throw e;
  }
}

export async function createOrder({ currency, billingCycle }) {
  return authedFetch("/billing/create-order", {
    method: "POST",
    body: JSON.stringify({ currency, billing_cycle: billingCycle }),
  });
}

export async function verifyPayment({ orderId, paymentId, signature }) {
  return authedFetch("/billing/verify-payment", {
    method: "POST",
    body: JSON.stringify({
      razorpay_order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    }),
  });
}

/**
 * Detect the user's preferred billing currency.
 * India (IST timezone) → INR; everything else → USD.
 */
export function detectCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return "INR";
    const lang = (navigator.language || "").toLowerCase();
    if (lang.endsWith("-in") || lang === "hi-in") return "INR";
  } catch { /* ignore */ }
  return "USD";
}

/**
 * Full checkout flow: creates order → opens Razorpay modal → verifies on success.
 * Resolves with the verify-payment response on success.
 * Rejects with Error on failure / dismissal.
 */
export async function openCheckout({
  currency,
  billingCycle,
  user,
  pricing,        // pricing dict from /billing/pricing — needed for key_id fallback
  onSuccess,
  onDismiss,
}) {
  const ok = await loadRazorpayScript();
  if (!ok) throw new Error("Couldn't load Razorpay. Check your connection.");

  const order = await createOrder({ currency, billingCycle });

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id || pricing?.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "MACHA",
      description: `Pro · ${billingCycle === "yearly" ? "Yearly" : "Monthly"}`,
      prefill: {
        name: user?.user_metadata?.full_name || user?.user_metadata?.name || "",
        email: user?.email || "",
      },
      theme: { color: "#6366f1" },
      handler: async (res) => {
        try {
          const result = await verifyPayment({
            orderId: res.razorpay_order_id,
            paymentId: res.razorpay_payment_id,
            signature: res.razorpay_signature,
          });
          onSuccess?.(result);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      },
      modal: {
        ondismiss: () => {
          onDismiss?.();
          reject(new Error("Checkout closed"));
        },
      },
    });
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}
